import React from 'react';
import {
  blankLine,
  buildVisitRows,
  computeCardValidity,
  formatCardDate,
  resolveAgentName,
  resolveCompanyMeta,
  resolveMemberTypeMark
} from './memberCardUtils';
import './MemberCardSheet.css';

const TYPE_MARKS = ['A', 'C', 'E'];

function CardField({ label, value }) {
  return (
    <div className="member-card__field">
      <span className="member-card__label">{label}</span>
      <span className="member-card__value">{blankLine(value)}</span>
    </div>
  );
}

export default function MemberCardSheet({
  member,
  visits = [],
  companies = [],
  getCompanyName,
  formatDate
}) {
  if (!member) return null;

  const company = resolveCompanyMeta(member, companies, getCompanyName);
  const validity = computeCardValidity(member, formatDate);
  const agentName = resolveAgentName(member);
  const activeMark = resolveMemberTypeMark(member.type);
  const visitRows = buildVisitRows(visits, formatDate);

  const birthLine = [
    formatCardDate(member.date_naissance, formatDate),
    member.lieu_naissance
  ]
    .filter(Boolean)
    .join(' — ');

  return (
    <article className="member-card" id="member-card-print-root" aria-label="Carte d'adhérent">
      <div className="member-card__sheet">
        {/* Verso — historique des visites */}
        <section className="member-card__panel member-card__panel--visits" aria-label="Visites effectuées">
          <header className="member-card__visits-title">VISITE EFFECTUÉE</header>
          <table className="member-card__visits-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Lieu de la visite</th>
                <th>Heure d&apos;entrée</th>
                <th>Heure de sortie</th>
              </tr>
            </thead>
            <tbody>
              {visitRows.map((row, index) => (
                <tr key={`visit-${index}`}>
                  <td>{blankLine(row.date, 8)}</td>
                  <td>{blankLine(row.place, 14)}</td>
                  <td>{blankLine(row.entry, 6)}</td>
                  <td>{blankLine(row.exit, 6)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="member-card__slogan">
            « Sécurité et santé : main dans la main vers le succès »
          </p>
        </section>

        {/* Recto — identité adhérent */}
        <section className="member-card__panel member-card__panel--identity" aria-label="Identité adhérent">
          <header className="member-card__brand">
            <div className="member-card__logo-wrap">
              <img
                src={`${process.env.PUBLIC_URL}/logo FUNTOA.png`}
                alt="FUNTOA — Service Médical Interentreprise"
                className="member-card__logo"
              />
            </div>
            <div className="member-card__photo" aria-label="Photo adhérent">
              <span>PHOTO</span>
            </div>
          </header>

          <div className="member-card__company-block">
            <div className="member-card__company-row">
              <span className="member-card__company-label">Société</span>
              <span className="member-card__company-value">{blankLine(company.name, 22)}</span>
            </div>
            <div className="member-card__company-row member-card__company-row--code">
              <span className="member-card__company-label">Code société :</span>
              <span className="member-card__company-value">{blankLine(company.code, 10)}</span>
            </div>
          </div>

          <h3 className="member-card__title">CARTE D&apos;ADHÉRENT</h3>

          <div className="member-card__fields">
            <CardField label="Nom :" value={member.nom} />
            <CardField label="Prénom :" value={member.prenom} />
            <CardField label="N° Matricule :" value={member.matricule} />
            <CardField label="Date/Lieu de naissance :" value={birthLine} />
            <CardField label="Adresse :" value={company.address} />
            <div className="member-card__field member-card__field--split">
              <div className="member-card__field-main">
                <span className="member-card__label">Ville :</span>
                <span className="member-card__value member-card__value--short">
                  {blankLine(company.city, 12)}
                </span>
              </div>
              <div className="member-card__field-main">
                <span className="member-card__label">Code Postal :</span>
                <span className="member-card__value member-card__value--short">
                  {blankLine(company.postalCode, 8)}
                </span>
              </div>
            </div>
            <CardField label="Nom de l'agent :" value={agentName} />
            <CardField label="Situation matrimoniale :" value={member.situation_matrimoniale} />
            <div className="member-card__footer-block">
              <div className="member-card__field member-card__field--validity">
                <span className="member-card__label">Validité de la carte : du</span>
                <span className="member-card__value member-card__value--inline">
                  {blankLine(validity.from, 10)}
                </span>
                <span className="member-card__label">au</span>
                <span className="member-card__value member-card__value--inline">
                  {blankLine(validity.to, 10)}
                </span>
              </div>

              <footer className="member-card__signatures">
                <div className="member-card__signature">
                  <span className="member-card__signature-label">Signature FUNTOA</span>
                </div>
                <div className="member-card__signature">
                  <span className="member-card__signature-label">Signature de l&apos;adhérent</span>
                </div>
              </footer>
            </div>
          </div>

          <div className="member-card__type-marks" aria-hidden>
            {TYPE_MARKS.map((mark) => (
              <span
                key={mark}
                className={`member-card__type-dot ${activeMark === mark ? 'is-active' : ''}`}
              >
                {mark}
              </span>
            ))}
          </div>

          <p className="member-card__slogan member-card__slogan--identity">
            « Sécurité et santé : main dans la main vers le succès »
          </p>
        </section>
      </div>
    </article>
  );
}
